import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../api";
import { usePermissions } from "../permissions";
import { Card, ErrorNotice, Loading } from "./Ui";

type State = {
  version: number;
  phase: number;
  integrationsPaused: boolean;
  archivedIds: number[];
  archivedReviews: Record<string, number>;
};
type Preview = {
  state: State;
  items: { id: number; name: string; team: string; version: number }[];
  reviews: { key: string; version: number }[];
};
const phases = [
  "Preparation — administrators only",
  "Phase 1 — S&E Analyst",
  "Phase 2 — Chief of Staff, CEO/MD and Board",
  "Phase 3 — department champions and deputies",
  "Phase 4 — department heads",
];
export function RolloutControls() {
  const permissions = usePermissions(),
    api = useApi();
  const query = useQuery({
    queryKey: ["rollout"],
    queryFn: () => api.get<Preview>("/rollout/"),
    enabled: permissions.can("roles.manage"),
  });
  if (!permissions.can("roles.manage")) return null;
  return (
    <Card className="span-2 business-roles">
      <h2>Phased rollout and clean start</h2>
      <p>
        Open access one phase at a time after the participating team signs off.
        Earlier phases remain enabled. Enable “Require rollout roles” in User
        access before opening Phase 1.
      </p>
      {query.isPending ? (
        <Loading />
      ) : query.isError ? (
        <ErrorNotice error={query.error} />
      ) : (
        <RolloutForm key={query.data.state.version} initial={query.data} />
      )}
    </Card>
  );
}
function RolloutForm({ initial }: { initial: Preview }) {
  const api = useApi(),
    client = useQueryClient();
  const [phase, setPhase] = useState(initial.state.phase),
    [paused, setPaused] = useState(initial.state.integrationsPaused),
    [reason, setReason] = useState(""),
    [backup, setBackup] = useState(""),
    [confirmation, setConfirmation] = useState(""),
    [ids, setIds] = useState<number[]>([]),
    [reviewKeys, setReviewKeys] = useState<string[]>([]),
    [restore, setRestore] = useState(false);
  const refresh = async () => {
    await client.invalidateQueries({ queryKey: ["rollout"] });
    await client.invalidateQueries({ queryKey: ["permissions-me"] });
    await client.invalidateQueries({ queryKey: ["initiatives"] });
    await client.invalidateQueries({ queryKey: ["dashboard-summary"] });
  };
  const config = useMutation({
    mutationFn: () =>
      api.put("/rollout/", {
        expectedVersion: initial.state.version,
        phase,
        integrationsPaused: paused,
        changeReason: reason,
      }),
    onSuccess: refresh,
  });
  const archive = useMutation({
    mutationFn: () =>
      api.post("/rollout/archive", {
        expectedVersion: initial.state.version,
        items: initial.items
          .filter((i) => ids.includes(i.id))
          .map(({ id, version }) => ({ id, version })),
        reviews: initial.reviews.filter((r) => reviewKeys.includes(r.key)),
        restore,
        backupReference: backup || undefined,
        changeReason: reason,
      }),
    onSuccess: refresh,
  });
  const shown = initial.items.filter(
    (i) => initial.state.archivedIds.includes(i.id) === restore,
  );
  const reviews = initial.reviews.filter(
    (r) =>
      (initial.state.archivedReviews[r.key] !== undefined &&
        r.version <= initial.state.archivedReviews[r.key]!) === restore,
  );
  const busy = config.isPending || archive.isPending;
  return (
    <div className="portfolio-form">
      <label>
        Current phase
        <select
          value={phase}
          onChange={(e) => setPhase(Number(e.target.value))}
        >
          {phases.map((name, i) => (
            <option key={name} value={i} disabled={i > initial.state.phase + 1}>
              {name}
            </option>
          ))}
        </select>
      </label>
      <label>
        <input
          type="checkbox"
          checked={paused}
          onChange={(e) => setPaused(e.target.checked)}
        />
        Pause delivery integrations and hide Linear-derived views
      </label>
      <p className="section-help">
        Pausing prevents new imports and hides Linear-derived delivery
        summaries. Dashboard-native Capacity health remains available.
        Credentials, configuration and historical snapshots remain
        available for later reactivation. An already-running provider request
        may finish; its data stays hidden while paused.
      </p>
      <label>
        Reason / phase acceptance evidence
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          minLength={3}
          maxLength={1000}
          placeholder="Record the team sign-off before advancing a phase"
        />
      </label>
      <button
        className="btn-primary"
        disabled={busy || reason.trim().length < 3}
        onClick={() => config.mutate()}
      >
        {config.isPending ? "Saving…" : "Save rollout settings"}
      </button>
      <details>
        <summary>Archive or restore initial portfolio records</summary>
        <p>
          Archiving hides selected records from the active portfolio, reports
          and direct links. Records, attachments and audit history stay in the
          database. New requests are not automatically archived. Verify a
          database backup before archiving.
        </p>
        <label>
          <input
            type="checkbox"
            checked={restore}
            onChange={(e) => {
              setRestore(e.target.checked);
              setIds([]);
              setReviewKeys([]);
              setConfirmation("");
            }}
          />
          Show archived records to restore
        </label>
        <div className="table-wrap">
          <table className="initiative-table">
            <thead>
              <tr>
                <th>Select</th>
                <th>ID</th>
                <th>Request</th>
                <th>Department</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((i) => (
                <tr key={i.id}>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`Select ${i.name}`}
                      checked={ids.includes(i.id)}
                      onChange={(e) =>
                        setIds((old) =>
                          e.target.checked
                            ? [...old, i.id]
                            : old.filter((id) => id !== i.id),
                        )
                      }
                    />
                  </td>
                  <td>{i.id}</td>
                  <td>{i.name}</td>
                  <td>{i.team}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!shown.length && <p>No matching portfolio records.</p>}
        {reviews.map((r) => (
          <label key={r.key}>
            <input
              type="checkbox"
              checked={reviewKeys.includes(r.key)}
              onChange={(e) =>
                setReviewKeys((old) =>
                  e.target.checked
                    ? [...old, r.key]
                    : old.filter((key) => key !== r.key),
                )
              }
            />
            {r.key} (strategic review notes)
          </label>
        ))}
        {!restore && (
          <label>
            Verified backup reference
            <input
              value={backup}
              onChange={(e) => setBackup(e.target.value)}
              maxLength={1000}
              placeholder="Backup ID / location and verification time — never paste credentials"
            />
          </label>
        )}
        <label>
          Type {restore ? "RESTORE" : "ARCHIVE"} to confirm{" "}
          {ids.length + reviewKeys.length} selected records
          <input
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
          />
        </label>
        <button
          className="btn-outline"
          disabled={
            busy ||
            reason.trim().length < 3 ||
            (!restore && !backup.trim()) ||
            (!ids.length && !reviewKeys.length) ||
            confirmation !== (restore ? "RESTORE" : "ARCHIVE")
          }
          onClick={() => archive.mutate()}
        >
          {archive.isPending
            ? "Applying…"
            : restore
              ? "Restore selected records"
              : "Archive selected records"}
        </button>
      </details>
      {(config.error || archive.error) && (
        <ErrorNotice error={(config.error || archive.error)!} />
      )}
    </div>
  );
}
